import { useEffect, useRef, useState } from "react";
import { currentLang, t } from "../../lib/sprache";

/**
 * CAPTCHA für Anmeldung, Registrierung und Passwort-Reset (Befund 9). Supabase Auth verlangt das Token, sobald CAPTCHA
 * eingeschaltet ist (docs/EINSPIELPLAN.md). Anbieter und öffentlicher Schlüssel kommen aus der Umgebung:
 * VITE_CAPTCHA_PROVIDER = „turnstile“ (Cloudflare) oder „hcaptcha“, VITE_CAPTCHA_SITE_KEY = Site-Key. Ohne beides zeigt die
 * Seite kein Widget und schickt kein Token (Demo, lokale Entwicklung). Tokens gelten einmal: nach jedem Versuch neu lösen.
 */

type Provider = "turnstile" | "hcaptcha";
interface Widget {
  render(el: HTMLElement, opts: Record<string, unknown>): string | number;
  reset(id?: string | number): void;
  remove?(id?: string | number): void;
}
declare global {
  interface Window { turnstile?: Widget; hcaptcha?: Widget }
}

export function captchaConfig(): { provider: Provider; siteKey: string } | null {
  const provider = (import.meta.env.VITE_CAPTCHA_PROVIDER as string | undefined)?.toLowerCase();
  const siteKey = import.meta.env.VITE_CAPTCHA_SITE_KEY as string | undefined;
  if (!siteKey || (provider !== "turnstile" && provider !== "hcaptcha")) return null;
  return { provider, siteKey };
}

const SCRIPT: Record<Provider, string> = {
  turnstile: "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit",
  hcaptcha: "https://js.hcaptcha.com/1/api.js?render=explicit&hl=de",
};
const loading: Partial<Record<Provider, Promise<void>>> = {};
function loadScript(p: Provider): Promise<void> {
  loading[p] ??= new Promise<void>((resolve, reject) => {
    const s = document.createElement("script");
    s.src = SCRIPT[p];
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error(t("Die Sicherheitsprüfung konnte nicht geladen werden.", "The security check could not be loaded.")));
    document.head.appendChild(s);
  });
  return loading[p]!;
}

/**
 * Widget: meldet das Token (oder null, wenn es abläuft). `resetKey` ändern setzt die Prüfung zurück (nach jedem Versuch).
 * Ohne Konfiguration rendert es nichts; die Seite fragt dann `captchaConfig()` und verlangt kein Token.
 */
export function CaptchaFeld({ onToken, resetKey }: { onToken: (t: string | null) => void; resetKey: number }) {
  const cfg = captchaConfig();
  const box = useRef<HTMLDivElement>(null);
  const id = useRef<string | number | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const cb = useRef(onToken);
  cb.current = onToken;

  useEffect(() => {
    if (!cfg || !box.current) return;
    let alive = true;
    loadScript(cfg.provider).then(() => {
      const w = cfg.provider === "turnstile" ? window.turnstile : window.hcaptcha;
      if (!alive || !w || !box.current || id.current !== null) return;
      id.current = w.render(box.current, {
        sitekey: cfg.siteKey,
        callback: (t: string) => { setFehler(null); cb.current(t); },
        "expired-callback": () => cb.current(null),
        // Befund 02.10.2026: Cloudflare lässt die Prüfung in ferngesteuerten Browsern (Erweiterungen, Automatisierung) und
        // manchmal mit VPN durchfallen; ohne Hinweis stand nur „Überprüfung fehlgeschlagen“ im Widget
        "error-callback": () => {
          cb.current(null);
          setFehler(t("Die Sicherheitsprüfung ist fehlgeschlagen. Lade die Seite neu. Hilft das nicht, öffne sie in einem anderen Browser, ohne Browser-Erweiterungen oder VPN.",
            "The security check failed. Reload the page. If that does not help, open it in another browser without browser extensions or VPN."));
        },
        // Dunkle Seite: Widget dunkel; der Rahmen selbst bleibt im hellen Farbschema (tk-captcha), sonst malt der Browser einen
        // weißen Rand um das Widget (Inhaber 06.10.2026, wie beim Google-Knopf)
        theme: dunkel() ? "dark" : "light",
        ...(cfg.provider === "turnstile" ? { language: currentLang() } : {}),
      });
    }).catch((e: Error) => alive && setFehler(e.message));
    return () => {
      alive = false;
    };
    // Konfiguration ist je Build fest
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!cfg || id.current === null || resetKey === 0) return;
    const w = cfg.provider === "turnstile" ? window.turnstile : window.hcaptcha;
    w?.reset(id.current);
    cb.current(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetKey]);

  if (!cfg) return null;
  return (
    <div className="stack">
      <div ref={box} className="tk-captcha" aria-label={t("Sicherheitsprüfung", "Security check")} />
      {fehler && <small className="tk-error" role="alert">{fehler}</small>}
    </div>
  );
}

function dunkel(): boolean {
  const theme = document.documentElement.dataset.theme;
  return theme === "dark" || (!theme && matchMedia("(prefers-color-scheme: dark)").matches);
}
